import React, { useState, useRef, useEffect } from 'react';
import {
  Upload,
  FileText,
  Download,
  Trash2,
  Eye,
  Search,
  AlertCircle,
  CheckCircle,
  Users,
  Filter,
  Send,
  Loader2
} from 'lucide-react';
import NavbarComptable from '../components/NavbarComptable';
import SidebarComptable from '../components/SidebarComptable';

// ✅ Configuration API identique à EcheancesComptable
const API_BASE = 'https://api-ec.cinef-expertises.com/api';

const EchangesFichiersComptable = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('tous');
  const [selectedClient, setSelectedClient] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formCategorie, setFormCategorie] = useState('comptable');
  const [formDescription, setFormDescription] = useState('');
  const [formClientId, setFormClientId] = useState('');
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fileInputRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [fichiers, setFichiers] = useState([]);

  const categories = [
    { value: 'tous', label: 'Tous les fichiers' },
    { value: 'fiscal', label: 'Documents fiscaux' },
    { value: 'comptable', label: 'Documents comptables' },
    { value: 'juridique', label: 'Documents juridiques' },
    { value: 'bancaire', label: 'Documents bancaires' }
  ];

  // --- Initial load
  useEffect(() => {
    fetchClients();
    fetchFichiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Fetch clients (corrigé)
  const fetchClients = async () => {
    try {
      const token = localStorage.getItem("expert_token");

      if (!token) {
        console.warn("Token expert manquant");
        return;
      }

      const res = await fetch(`${API_BASE}/clients`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      // ✅ Vérification du statut HTTP
      if (!res.ok) {
        throw new Error(`Erreur HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      const clientsList = data?.data ?? data;

      setClients(Array.isArray(clientsList) ? clientsList : []);

      // Sélectionner le premier client par défaut
      const first = Array.isArray(clientsList) && clientsList.length ? clientsList[0] : null;
      setSelectedClient(first);
      setFormClientId(first?.id ?? '');

    } catch (err) {
      console.error('Erreur lors du chargement des clients:', err);
      showNotification('Impossible de charger les clients', 'error');
      setClients([]);
    }
  };

  // ✅ Fetch fichiers (corrigé)
  const fetchFichiers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("expert_token");

      if (!token) {
        console.warn("Token expert manquant");
        return;
      }

      const res = await fetch(`${API_BASE}/fichiers`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      // ✅ Vérification du statut HTTP
      if (!res.ok) {
        throw new Error(`Erreur HTTP ${res.status}: ${res.statusText}`);
      }

      const data = await res.json();
      const fichiersList = data?.data ?? data;

      setFichiers(Array.isArray(fichiersList) ? fichiersList : []);

    } catch (err) {
      console.error('Erreur lors du chargement des fichiers:', err);
      showNotification('Impossible de charger les fichiers', 'error');
      setFichiers([]);
    } finally {
      setLoading(false);
    }
  };

  // Statistiques calculées
  const stats = {
    nouveau: fichiers.filter(f => f.statut === 'nouveau').length,
    lu: fichiers.filter(f => f.statut === 'lu').length,
    traite: fichiers.filter(f => f.statut === 'traite').length
  };

  // Filtrage des fichiers
  const filteredFichiers = fichiers.filter(fichier => {
    const matchesSearch = fichier.nom_fichier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fichier.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'tous' || fichier.categorie === selectedCategory;
    const matchesClient = !selectedClient || fichier.destinataire_id === selectedClient.id;

    return matchesSearch && matchesCategory && matchesClient;
  });

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'image/jpeg',
      'image/png'
    ];

    if (!allowedTypes.includes(file.type)) {
      showNotification('Type de fichier non autorisé. Formats acceptés: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG', 'error');
      event.target.value = '';
      return;
    }

    // Limite de taille: 10MB
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      showNotification('Fichier trop volumineux (max: 10MB)', 'error');
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    setShowForm(true);
  };

  // ✅ Upload confirmé (avec spinner)
  const handleUploadConfirm = async () => {
    if (!selectedFile || !formClientId) {
      showNotification('Veuillez sélectionner un client', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('destinataire_id', formClientId);
    formData.append('fichier', selectedFile);
    formData.append('categorie', formCategorie);
    formData.append('description', formDescription || 'Document partagé');

    setUploadProgress(true);

    try {
      const token = localStorage.getItem('expert_token');

      if (!token) {
        showNotification('Token manquant. Veuillez vous reconnecter.', 'error');
        return;
      }

      const res = await fetch(`${API_BASE}/fichiers/upload`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
          // ⚠️ Ne PAS mettre Content-Type avec FormData
        },
        body: formData
      });

      // ✅ Vérification du statut
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ message: 'Erreur serveur' }));
        throw new Error(errorData.message || `Erreur HTTP ${res.status}`);
      }

      const data = await res.json();

      if (data.success) {
        showNotification("Fichier envoyé avec succès !");
        setShowForm(false);
        setSelectedFile(null);
        setFormDescription('');
        setFormCategorie('comptable');

        // Recharger les fichiers
        await fetchFichiers();
      } else {
        showNotification(data.message || 'Erreur lors de l\'envoi', 'error');
      }
    } catch (e) {
      console.error("Erreur upload:", e);
      showNotification(e.message || "Erreur lors de l'upload", 'error');
    } finally {
      setUploadProgress(false);
    }
  };

  // ✅ Download (avec spinner)
  const handleDownload = async (fichier) => {
    setDownloadingId(fichier.id);
    try {
      const token = localStorage.getItem("expert_token");
      const res = await fetch(`${API_BASE}/fichiers/${fichier.id}/download`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fichier.nom_fichier);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showNotification('Fichier téléchargé avec succès !');
    } catch (err) {
      console.error("Erreur téléchargement:", err);
      showNotification("Impossible de télécharger le fichier", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  // ✅ Delete (avec spinner)
  const handleDelete = async (fichier) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce fichier ?")) return;

    setDeletingId(fichier.id);
    try {
      const token = localStorage.getItem("expert_token");
      const res = await fetch(`${API_BASE}/fichiers/${fichier.id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      if (!res.ok) {
        throw new Error(`Erreur HTTP ${res.status}`);
      }

      const data = await res.json();

      if (data.success) {
        showNotification("Fichier supprimé avec succès !");
        setFichiers(fichiers.filter(f => f.id !== fichier.id));
      } else {
        showNotification(data.message || "Erreur lors de la suppression", 'error');
      }
    } catch (err) {
      console.error("Erreur suppression:", err);
      showNotification("Impossible de supprimer ce fichier", 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return '📄';
      case 'excel': return '📊';
      case 'word': return '📝';
      case 'image': return '🖼️';
      default: return '📄';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <>
      <div className="app-container">
        <SidebarComptable
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />

        <div className="main-wrapper">
          <NavbarComptable
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
            clients={clients}
          />

          <main className="main-content">
            <div className="content-container">
            {/* Notification */}
            {notification && (
              <div className={`notification ${notification.type}`}>
                {notification.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                <span>{notification.message}</span>
              </div>
            )}

            {/* Header Section */}
            <div className="page-header">
              <div className="header-content">
                <div className="header-text">
                  <h1 className="page-title">Échanges de Fichiers</h1>
                  <p className="page-subtitle">
                    Partagez et gérez les documents de vos clients en toute sécurité
                  </p>
                </div>
              </div>
            </div>

            {/* Client Filter Alert */}
            {selectedClient && (
              <div className="client-filter-alert">
                <Users size={20} />
                <span>Fichiers filtrés pour : <strong>{selectedClient.nom || selectedClient.name}</strong></span>
                <button onClick={() => setSelectedClient(null)} className="clear-filter">
                  Voir tous les clients
                </button>
              </div>
            )}

            {/* Upload Section */}
            <div className="upload-section">
              <div className="upload-zone" onClick={handleUploadClick}>
                <Upload size={48} className="upload-icon" />
                <h3 className="upload-title">
                  {uploadProgress ? 'Upload en cours...' : 'Envoyer un fichier à un client'}
                </h3>
                <p className="upload-subtitle">Cliquez pour parcourir vos fichiers</p>
                <button className="btn-upload" disabled={uploadProgress}>
                  {uploadProgress ? (
                    <>
                      <Loader2 size={18} className="spinner" />
                      Chargement...
                    </>
                  ) : (
                    'Sélectionner un fichier'
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg"
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {/* Upload Form */}
            {showForm && (
              <div className="form-popup">
                <div className="form-content">
                  <h3>📤 Envoyer un fichier</h3>
                  <p className="file-name-display">
                    <FileText size={18} />
                    <strong>{selectedFile?.name}</strong>
                  </p>

                  <label>
                    <Users size={16} /> Client destinataire *
                  </label>
                  <select
                    className="form-control"
                    value={formClientId}
                    onChange={(e) => {
                      setFormClientId(e.target.value);
                      const client = clients.find(c => c.id === parseInt(e.target.value));
                      setSelectedClient(client);
                    }}
                    required
                  >
                    <option value="">-- Sélectionner un client --</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.nom || client.name} {client.type && `(${client.type})`}
                      </option>
                    ))}
                  </select>

                  <label>
                    <Filter size={16} /> Catégorie
                  </label>
                  <select
                    className="form-control"
                    value={formCategorie}
                    onChange={(e) => setFormCategorie(e.target.value)}
                  >
                    <option value="comptable">Comptable</option>
                    <option value="fiscal">Fiscal</option>
                    <option value="juridique">Juridique</option>
                    <option value="bancaire">Bancaire</option>
                  </select>

                  <label>
                    <FileText size={16} /> Description / Objet
                  </label>
                  <textarea
                    className="form-control"
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Décrivez brièvement le contenu du fichier..."
                  />

                  <button
                    className="btn primary"
                    onClick={handleUploadConfirm}
                    disabled={uploadProgress}
                  >
                    {uploadProgress ? (
                      <>
                        <Loader2 size={18} className="spinner" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Envoyer le fichier
                      </>
                    )}
                  </button>

                  <button
                    className="btn danger"
                    onClick={() => {
                      setShowForm(false);
                      setSelectedFile(null);
                    }}
                    disabled={uploadProgress}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Filters Section */}
            <div className="filters-section">
              <div className="search-box">
                <Search size={20} className="search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher un fichier..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-select"
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card nouveau">
                <div className="stat-content">
                  <div className="stat-info">
                    <h3 className="stat-title">Nouveaux</h3>
                    <p className="stat-value">{stats.nouveau}</p>
                  </div>
                  <div className="stat-icon">
                    <FileText size={40} />
                  </div>
                </div>
              </div>

              <div className="stat-card lu">
                <div className="stat-content">
                  <div className="stat-info">
                    <h3 className="stat-title">Lus</h3>
                    <p className="stat-value">{stats.lu}</p>
                  </div>
                  <div className="stat-icon">
                    <Eye size={40} />
                  </div>
                </div>
              </div>

              <div className="stat-card traite">
                <div className="stat-content">
                  <div className="stat-info">
                    <h3 className="stat-title">Traités</h3>
                    <p className="stat-value">{stats.traite}</p>
                  </div>
                  <div className="stat-icon">
                    <Download size={40} />
                  </div>
                </div>
              </div>
            </div>

            {/* Files List */}
            <div className="fichiers-container">
              <div className="container-header">
                <h2 className="container-title">
                  Fichiers partagés ({filteredFichiers.length})
                </h2>
              </div>

              {loading ? (
                <div className="empty-state">
                  <Loader2 size={48} className="spinner" />
                  <p>Chargement des fichiers...</p>
                </div>
              ) : filteredFichiers.length === 0 ? (
                <div className="empty-state">
                  <FileText size={64} className="empty-icon" />
                  <h3>Aucun fichier trouvé</h3>
                  <p>
                    {selectedClient
                      ? `Aucun fichier pour ${selectedClient.nom || selectedClient.name}`
                      : 'Commencez par envoyer un document à un client'}
                  </p>
                </div>
              ) : (
                <div className="fichiers-list">
                  {filteredFichiers.map((fichier) => (
                    <div key={fichier.id} className="fichier-item">
                      <div className="fichier-content">
                        <div className="fichier-main">
                          <div className="file-icon">
                            {getFileIcon(fichier.type_fichier)}
                          </div>
                          <div className="fichier-info">
                            <h4 className="fichier-title">{fichier.nom_fichier}</h4>
                            <p className="fichier-description">{fichier.description}</p>

                            <div className="fichier-meta">
                              <span className="fichier-date">
                                {formatDate(fichier.created_at)}
                              </span>

                              {fichier.taille_fichier && (
                                <span className="fichier-taille">
                                  {fichier.taille_fichier}
                                </span>
                              )}

                              <span className="fichier-client">
                                <Users size={14} />
                                {fichier.destinataire?.nom || fichier.destinataire?.name || '—'}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="fichier-actions">
                          <div className="fichier-status-section">
                            <span className={`status-badge ${fichier.statut}`}>
                              {fichier.statut?.toUpperCase()}
                            </span>
                          </div>

                          <div className="action-buttons">
                            <button
                              className="btn-action secondary"
                              onClick={() => handleDownload(fichier)}
                              disabled={downloadingId === fichier.id}
                            >
                              {downloadingId === fichier.id ? (
                                <>
                                  <Loader2 size={14} className="spinner" />
                                  Chargement...
                                </>
                              ) : (
                                <>
                                  <Download size={14} />
                                  Télécharger
                                </>
                              )}
                            </button>
                            <button
                              className="btn-action danger"
                              onClick={() => handleDelete(fichier)}
                              disabled={deletingId === fichier.id}
                            >
                              {deletingId === fichier.id ? (
                                <>
                                  <Loader2 size={14} className="spinner" />
                                  Suppression...
                                </>
                              ) : (
                                <>
                                  <Trash2 size={14} />
                                  Supprimer
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          </main>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        html, body {
          height: auto !important;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          max-width: 100vw;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .app-container {
          max-width: 100vw;
          overflow-x: hidden;
        }

        /* ✅ Animation Spinner */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        .app-container {
          display: flex;
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafb 0%, #f1f5f9 100%);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          overflow-x: hidden;
          max-width: 100vw;
        }

        .main-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          margin-left: 280px;
          max-width: calc(100vw - 280px);
          overflow-x: hidden;
        }

        .main-content {
          flex: 1;
          padding: 32px 24px;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: rgba(220, 53, 69, 0.3) transparent;
          max-width: 100%;
          width: 100%;
          box-sizing: border-box;
        }

        .main-content::-webkit-scrollbar {
          width: 6px;
        }

        .main-content::-webkit-scrollbar-thumb {
          background: rgba(220, 53, 69, 0.2);
          border-radius: 10px;
        }

        /* Content Container */
        .content-container {
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
          padding: 0;
          box-sizing: border-box;
        }

        /* Notification */
        .notification {
          position: fixed;
          top: 100px;
          right: 24px;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 24px;
          border-radius: 12px;
          background: white;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          font-weight: 600;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from { transform: translateX(400px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .notification.success {
          border-left: 4px solid #10b981;
          color: #059669;
        }

        .notification.error {
          border-left: 4px solid #dc3545;
          color: #b91c1c;
        }

        /* Client Filter Alert */
        .client-filter-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 24px;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.1), rgba(0, 138, 68, 0.1));
          border: 1px solid rgba(220, 53, 69, 0.2);
          border-radius: 12px;
          margin-bottom: 24px;
          color: #1e293b;
          font-weight: 500;
        }

        .clear-filter {
          margin-left: auto;
          padding: 8px 16px;
          background: white;
          border: 1px solid #dc3545;
          color: #dc3545;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .clear-filter:hover {
          background: #dc3545;
          color: white;
        }

        /* Header */
        .page-header {
          margin-bottom: 32px;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .page-title {
          font-size: 32px;
          font-weight: 700;
          margin: 0 0 6px 0;
          background: linear-gradient(135deg, #dc3545, #008A44);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1.2;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          color: #64748b;
          margin: 0;
          font-size: 16px;
          font-weight: 500;
        }

        /* Upload Section */
        .upload-section {
          margin-bottom: 32px;
        }

        .upload-zone {
          background: #ffffff;
          border: 2px dashed #d1d5db;
          border-radius: 16px;
          padding: 40px;
          text-align: center;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .upload-zone:hover {
          border-color: #dc3545;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.02) 0%, rgba(0, 138, 68, 0.02) 100%);
        }

        .upload-icon {
          color: #9ca3af;
          margin-bottom: 16px;
        }

        .upload-title {
          font-size: 20px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 8px 0;
        }

        .upload-subtitle {
          color: #6b7280;
          margin: 0 0 24px 0;
          font-size: 14px;
        }

        .btn-upload {
          background: linear-gradient(135deg, #dc3545, #b91c1c);
          color: #ffffff;
          border: none;
          padding: 12px 24px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: inherit;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .btn-upload:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(220, 53, 69, 0.3);
        }

        .btn-upload:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Form Popup */
        .form-popup {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }

        .form-content {
          background: white;
          padding: 35px;
          border-radius: 16px;
          width: 550px;
          max-width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .form-content h3 {
          margin: 0 0 25px 0;
          font-size: 24px;
          font-weight: 600;
          color: #1a1a1a;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .file-name-display {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: #f8fafb;
          border-radius: 8px;
          margin-bottom: 20px;
          color: #1e293b;
          font-size: 14px;
        }

        .form-content label {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          font-weight: 500;
          color: #333;
          font-size: 14px;
        }

        .form-control {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e0e0e0;
          border-radius: 10px;
          font-size: 15px;
          margin-bottom: 20px;
          transition: all 0.3s ease;
          font-family: inherit;
        }

        .form-control:focus {
          outline: none;
          border-color: #dc3545;
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
        }

        textarea.form-control {
          resize: vertical;
          min-height: 80px;
        }

        .btn {
          width: 100%;
          padding: 14px 24px;
          border-radius: 10px;
          margin-top: 10px;
          font-size: 16px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .btn.primary {
          background: linear-gradient(135deg, #dc3545 0%, #b91c1c 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
        }

        .btn.primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(220, 53, 69, 0.4);
        }

        .btn.primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn.danger {
          background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
        }

        .btn.danger:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(107, 114, 128, 0.4);
        }

        .btn.danger:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Filters */
        .filters-section {
          display: flex;
          gap: 16px;
          margin-bottom: 32px;
          align-items: center;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 12px 16px 12px 48px;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          background: #ffffff;
          font-size: 14px;
          font-family: inherit;
          transition: all 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #dc3545;
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
        }

        .category-select {
          padding: 12px 16px;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          background: #ffffff;
          font-size: 14px;
          font-family: inherit;
          cursor: pointer;
          min-width: 200px;
        }

        .category-select:focus {
          outline: none;
          border-color: #dc3545;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: #ffffff;
          padding: 28px;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: currentColor;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
        }

        .stat-card.nouveau {
          color: #dc3545;
        }

        .stat-card.lu {
          color: #f59e0b;
        }

        .stat-card.traite {
          color: #10b981;
        }

        .stat-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .stat-info {
          flex: 1;
        }

        .stat-title {
          font-size: 16px;
          font-weight: 600;
          color: currentColor;
          margin: 0 0 12px 0;
          opacity: 0.9;
        }

        .stat-value {
          font-size: 36px;
          font-weight: 700;
          color: currentColor;
          margin: 0;
          line-height: 1;
        }

        .stat-icon {
          opacity: 0.8;
          color: currentColor;
        }

        /* Files Container */
        .fichiers-container {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid rgba(0, 0, 0, 0.05);
          overflow: hidden;
          margin-bottom: 40px;
        }

        .container-header {
          padding: 24px 28px;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(0, 138, 68, 0.05) 100%);
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .container-title {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
          letter-spacing: -0.3px;
        }

        .fichiers-list {
          padding: 0;
        }

        .fichier-item {
          padding: 24px 28px;
          border-bottom: 1px solid #f1f5f9;
          transition: all 0.2s ease;
        }

        .fichier-item:last-child {
          border-bottom: none;
        }

        .fichier-item:hover {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }

        .fichier-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
          width: 100%;
          max-width: 100%;
          overflow: hidden;
        }

        .fichier-main {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          flex: 1;
          min-width: 0;
          overflow: hidden;
          max-width: calc(100% - 300px);
        }

        .file-icon {
          font-size: 32px;
          margin-top: 4px;
          flex-shrink: 0;
        }

        .fichier-info {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          max-width: 100%;
        }

        .fichier-title {
          font-size: 17px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 6px 0;
          line-height: 1.3;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
        }

        .fichier-description {
          font-size: 14px;
          color: #64748b;
          margin: 0 0 12px 0;
          line-height: 1.4;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          max-width: 100%;
        }

        .fichier-meta {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
          max-width: 100%;
          overflow: hidden;
        }

        .fichier-client,
        .fichier-date,
        .fichier-uploader,
        .fichier-taille {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
        }

        .fichier-client {
          color: #dc3545;
          font-weight: 600;
        }

        .fichier-actions {
          display: flex;
          align-items: center;
          gap: 20px;
          flex-shrink: 0;
          min-width: 250px;
          max-width: 300px;
        }

        .fichier-status-section {
          text-align: right;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge.nouveau {
          background: linear-gradient(135deg, #dc3545, #b91c1c);
          color: #ffffff;
        }

        .status-badge.lu {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: #ffffff;
        }

        .status-badge.traite {
          background: linear-gradient(135deg, #10b981, #059669);
          color: #ffffff;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          min-width: 200px;
        }

        .btn-action {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          border: none;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .btn-action.primary {
          background: linear-gradient(135deg, #10b981, #059669);
          color: #ffffff;
        }

        .btn-action.primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .btn-action.secondary {
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          color: #ffffff;
        }

        .btn-action.secondary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }

        .btn-action.danger {
          background: linear-gradient(135deg, #dc3545, #b91c1c);
          color: #ffffff;
        }

        .btn-action.danger:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
        }

        /* Empty State */
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }

        .empty-icon {
          color: #d1d5db;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          font-size: 18px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 8px 0;
        }

        .empty-state p {
          color: #9ca3af;
          margin: 0;
        }

        /* Responsive Design */
        @media (max-width: 1600px) {
          .content-container {
            max-width: 1200px;
          }
        }

        @media (max-width: 1400px) {
          .content-container {
            max-width: 1100px;
          }
        }

        @media (max-width: 1200px) {
          .content-container {
            max-width: 100%;
            padding: 0 16px;
          }
        }

        @media (max-width: 1023px) {
          .app-container {
            min-height: 100vh;
            height: auto;
            overflow-x: hidden;
          }

          .main-wrapper {
            margin-left: 0;
            max-width: 100vw;
          }

          .main-content {
            padding: 20px;
            position: relative;
            overflow-x: hidden;
            max-width: 100%;
          }

          .content-container {
            padding: 0 8px;
          }

          .fichier-main {
            max-width: calc(100% - 250px);
          }

          .fichier-actions {
            min-width: 200px;
            max-width: 250px;
          }

          .page-title {
            font-size: 28px;
          }

          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 16px;
          }

          .stat-card {
            padding: 20px;
          }
        }

        @media (max-width: 768px) {
          .app-container {
            min-height: 100vh;
            height: auto;
            overflow-x: hidden;
            max-width: 100vw;
          }

          .main-content {
            padding: 16px;
            position: relative;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
            touch-action: manipulation;
            max-width: 100%;
            width: 100%;
          }

          .content-container {
            padding: 0;
          }

          .fichier-main {
            max-width: 100%;
            flex: 1;
          }

          .fichier-actions {
            min-width: auto;
            max-width: none;
            width: 100%;
          }

          .header-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .page-title {
            font-size: 24px;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            max-width: none;
          }

          .fichier-content {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
          }

          .fichier-actions {
            flex-direction: column;
            gap: 12px;
          }

          .action-buttons {
            flex-direction: column;
          }

          .btn-action {
            justify-content: center;
          }

          .client-filter-alert {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .clear-filter {
            margin-left: 0;
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .fichiers-container {
            border-radius: 12px;
            overflow-x: hidden;
          }

          .container-header,
          .fichier-item {
            padding: 20px;
          }

          .fichier-main {
            max-width: 100%;
          }

          .fichier-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .upload-zone {
            padding: 32px 20px;
          }

          .form-content {
            padding: 25px;
          }

          .action-buttons {
            flex-direction: column;
            width: 100%;
          }

          .btn-action {
            justify-content: center;
            width: 100%;
          }
        }
      `}</style>
    </>
  );
}
export default EchangesFichiersComptable;